/**
 * Student session for the lecture portal.
 *
 * Kept deliberately separate from the staff session ("current_user") so that a
 * student signing in on a shared machine can never overwrite or inherit a
 * staff session, and vice versa. Everything that touches the stored session
 * goes through this module -- no component reads localStorage directly.
 */

const STUDENT_SESSION_KEY = "student_session"

/**
 * @typedef {Object} StudentSession
 * @property {string} token
 * @property {Object} student_data
 */

/** @returns {StudentSession|null} */
export const getStudentSession = () => {
    try {
        const raw = localStorage.getItem(STUDENT_SESSION_KEY)
        if (!raw) return null
        const session = JSON.parse(raw)
        return session?.token ? session : null
    } catch (error) {
        return null
    }
}

export const getStudentToken = () => getStudentSession()?.token || null

/** The signed-in student's profile, or null. */
export const getCurrentStudent = () => getStudentSession()?.student_data || null

/** The class the signed-in student belongs to -- drives every lecture query. */
export const getStudentClassId = () => {
    const student = getCurrentStudent()
    return student?.class_id || student?.meta_data?.class_id || null
}

export const setStudentSession = ({ token, student_data }) => {
    localStorage.setItem(
        STUDENT_SESSION_KEY,
        JSON.stringify({ token, student_data })
    )
}

export const clearStudentSession = () => {
    localStorage.removeItem(STUDENT_SESSION_KEY)
}

export const isStudentAuthenticated = () => Boolean(getStudentToken())
